import React, { useState, useEffect, useRef } from "react";
import { TextField, Button, MenuItem, Chip, Box, Badge, Typography, Alert, LinearProgress } from "@mui/material";
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import Paper from '@mui/material/Paper';

import { DateCalendar } from "@mui/x-date-pickers/DateCalendar";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { TimeField } from "@mui/x-date-pickers/TimeField";
import { PickersDay } from "@mui/x-date-pickers/PickersDay";
import dayjs from "dayjs";
import 'dayjs/locale/de';

import { useSnackbar } from "../providers/SnackbarProvider";
import axiosInstance from "./axiosInstance";

dayjs.locale('de');

// Custom day component with badge for days with entries
function ServerDay(props) {
  const { highlightedDays = [], day, outsideCurrentMonth, ...other } = props;

  const isHighlighted =
    !props.outsideCurrentMonth && highlightedDays.includes(props.day.format('YYYY-MM-DD'));

  return (
    <Badge
      key={props.day.toString()}
      overlap="circular"
      badgeContent={isHighlighted ? <CheckCircleIcon color="success" fontSize="small" /> : undefined}
    >
      <PickersDay {...other} outsideCurrentMonth={outsideCurrentMonth} day={day} />
    </Badge>
  );
}

const TimeEntryForm = ({ onEntryAdded }) => {
  const { showSnackbar } = useSnackbar();
  const [availableAssignments, setAvailableAssignments] = useState([]);
  const [selectedAssignment, setSelectedAssignment] = useState("");
  const [hours, setHours] = useState(null);
  const [description, setDescription] = useState("");
  const [selectedDates, setSelectedDates] = useState([]);
  const [highlightedDays, setHighlightedDays] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [budgetInfo, setBudgetInfo] = useState(null);
  const requestAbortController = useRef(null);

  // Load available assignments on mount
  useEffect(() => {
    fetchAvailableAssignments();
  }, []);

  // Fetch available assignments using the new endpoint
  const fetchAvailableAssignments = async () => {
    try {
      const response = await axiosInstance.get("/time-entries/available_assignments/", {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("access_token")}`,
        },
      });
      
      setAvailableAssignments(response.data);
      
      // Auto-select if only one assignment available
      if (response.data.length === 1) {
        setSelectedAssignment(response.data[0].id);
      }
    } catch (error) {
      console.error("Error loading available assignments:", error);
      showSnackbar("Fehler beim Laden der verfügbaren Projekte", "error");
    }
  };

  // Fetch budget info when assignment is selected
  useEffect(() => {
    if (selectedAssignment) {
      fetchBudgetInfo(selectedAssignment);
    } else {
      setBudgetInfo(null);
    }
  }, [selectedAssignment]);

  // Fetch budget information for selected assignment
  const fetchBudgetInfo = async (assignmentId) => {
    try {
      const response = await axiosInstance.get(`/time-entries/?consultant_assignment=${assignmentId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` },
      });
      
      // Find the selected assignment to get total hours
      const assignment = availableAssignments.find(a => a.id === assignmentId);
      if (!assignment) return;

      const totalBudgetHours = Number(assignment.hours) || 0;
      const usedHours = response.data.reduce((sum, entry) => sum + parseFloat(entry.hours || 0), 0);
      const remainingHours = totalBudgetHours - usedHours;
      const percentageUsed = totalBudgetHours > 0 ? (usedHours / totalBudgetHours) * 100 : 0;

      setBudgetInfo({
        totalBudgetHours: Number(totalBudgetHours),
        usedHours: Number(usedHours),
        remainingHours: Number(remainingHours),
        percentageUsed: Number(percentageUsed)
      });
    } catch (error) {
      console.error("Error fetching budget info:", error);
      setBudgetInfo(null);
    }
  };

  // Function to fetch days with time entries
  const fetchHighlightedDays = async (date) => {
    try {
      setIsLoading(true);
      const response = await axiosInstance.get('/time-entries/', {
        headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` },
      });
      
      // Extract unique dates from time entries
      const dates = [...new Set(response.data.map(entry => entry.date))];
      setHighlightedDays(dates);
      setIsLoading(false);
    } catch (error) {
      console.error("Error fetching highlighted days:", error);
      setIsLoading(false);
    }
  };

  // Function to add a date to the list
  const handleDateChange = (newDate) => {
    const formattedDate = dayjs(newDate).format("YYYY-MM-DD");
    if (!selectedDates.includes(formattedDate)) {
      setSelectedDates([...selectedDates, formattedDate]);
    }
  };

  // Function to remove a date from the list
  const handleRemoveDate = (dateToRemove) => {
    setSelectedDates(selectedDates.filter((date) => date !== dateToRemove));
  };

  // Get warning level based on budget
  const getBudgetWarningLevel = () => {
    if (!budgetInfo) return null;
    
    const { remainingHours, percentageUsed } = budgetInfo;
    
    if (remainingHours <= 0) {
      return { severity: 'error', message: 'Budget aufgebraucht! Keine Stunden mehr verfügbar.' };
    } else if (percentageUsed >= 90) {
      return { severity: 'error', message: `Achtung! Nur noch ${remainingHours.toFixed(1)}h verfügbar (${(100 - percentageUsed).toFixed(1)}% des Budgets).` };
    } else if (percentageUsed >= 75) {
      return { severity: 'warning', message: `Warnung: Noch ${remainingHours.toFixed(1)}h verfügbar (${(100 - percentageUsed).toFixed(1)}% des Budgets).` };
    } else if (percentageUsed >= 50) {
      return { severity: 'info', message: `Noch ${remainingHours.toFixed(1)}h verfügbar (${(100 - percentageUsed).toFixed(1)}% des Budgets).` };
    }
    
    return null;
  };

  // Check if submission would exceed budget
  const wouldExceedBudget = () => {
    if (!budgetInfo || !hours) return false;
    
    const hoursValue = parseFloat(hours.format("HH:mm").split(":")[0]) + 
                      parseFloat(hours.format("HH:mm").split(":")[1]) / 60;
    const totalNewHours = hoursValue * selectedDates.length;
    
    return (budgetInfo.remainingHours - totalNewHours) < 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!selectedAssignment || !hours || selectedDates.length === 0) {
      showSnackbar("Bitte wählen Sie ein Projekt, Stunden und mindestens ein Datum!", "warning");
      return;
    }

    // Check if budget would be exceeded
    if (wouldExceedBudget()) {
      const hoursValue = parseFloat(hours.format("HH:mm").split(":")[0]) + 
                        parseFloat(hours.format("HH:mm").split(":")[1]) / 60;
      const totalNewHours = hoursValue * selectedDates.length;
      
      if (!window.confirm(
        `Warnung: Diese Eingabe (${totalNewHours.toFixed(1)}h) würde das verbleibende Budget (${budgetInfo.remainingHours.toFixed(1)}h) überschreiten. Trotzdem fortfahren?`
      )) {
        return;
      }
    }

    try {
      // Calculate hours from the time field
      const hoursValue = parseFloat(hours.format("HH:mm").split(":")[0]) + 
                        parseFloat(hours.format("HH:mm").split(":")[1]) / 60;
      
      const requests = selectedDates.map((date) =>
        axiosInstance.post("/time-entries/", {
          consultant_assignment: selectedAssignment,
          hours: hoursValue,
          date: date,
          description: description
        })
      );

      await Promise.all(requests);
      onEntryAdded(); // Update table
      setHours(null);
      setSelectedAssignment("");
      setSelectedDates([]);
      setDescription("");
      
      // Refresh highlighted days and budget info after adding new entries
      fetchHighlightedDays(dayjs());
      fetchAvailableAssignments(); // Refresh assignments to update worked hours
      showSnackbar("Zeiteinträge erfolgreich gespeichert", "success");
    } catch (error) {
      console.error("Error saving time entry:", error);
      showSnackbar("Fehler beim Speichern der Zeiteinträge", "error");
    }
  };

  // Handle month change in calendar
  const handleMonthChange = (date) => {
    fetchHighlightedDays(date);
  };

  // Initial fetch of highlighted days
  useEffect(() => {
    fetchHighlightedDays(dayjs());
    
    // Cleanup on unmount
    return () => {
      if (requestAbortController.current) {
        requestAbortController.current.abort();
      }
    };
  }, []);

  // Helper to get assignment label with project info
  const getAssignmentLabel = (assignment) => {
    return `${assignment.project_name} (${assignment.account_name}) - ${assignment.remaining_hours.toFixed(1)}h / ${assignment.hours}h verbleibend`;
  };

  // Group assignments by project for better overview
  const getGroupedAssignments = () => {
    const grouped = {};
    availableAssignments.forEach(assignment => {
      const key = assignment.project_id;
      if (!grouped[key]) {
        grouped[key] = {
          project_name: assignment.project_name,
          account_name: assignment.account_name,
          assignments: []
        };
      }
      grouped[key].assignments.push(assignment);
    });
    return grouped;
  };

  const warningLevel = getBudgetWarningLevel();
  const groupedAssignments = getGroupedAssignments();

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="de">



      <Box
        display="flex"
        flexDirection={{ xs: "column", md: "row" }}
        gap={2}
        justifyContent="space-evenly"
        alignItems="stretch" // wichtig für vertikalen Divider
      >


      <Paper 
        elevation={1}
        sx={{ 
          mb: 1, 
          p: 2,
          bgcolor: 'background.paper', 
          borderRadius: 2 
        }}
      >   


        {/* Calendar */}
        <Box flexBasis={{ xs: "100%", md: "20%" }}>
          <DateCalendar 
            onChange={handleDateChange} 
            onMonthChange={handleMonthChange}
            slots={{ day: ServerDay }}
            slotProps={{ day: { highlightedDays } }}
          />
        </Box>

      </Paper>


        <Paper 
          elevation={1}
          sx={{ 
            width: '100%',
            mb: 1, 
            p: 2,
            bgcolor: 'background.paper', 
            borderRadius: 2 
          }}
        >   

          {/* Form */}
          <Box flexBasis={{ xs: "100%", md: "80%" }} display="flex" flexDirection="column" gap={2}>          
            {/* Display selected dates as chips */}
            <Box display="flex" gap={1} flexWrap="wrap">
              {selectedDates.map((date) => (
                <Chip
                  key={date}
                  label={date}
                  onDelete={() => handleRemoveDate(date)}
                  color="primary"
                />
              ))}
            </Box>


            {/* Budget Information Display */}
            {budgetInfo && (
              <Box sx={{ mb: 1 }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                  <Typography variant="body2" color="text.secondary">
                    Zeitbudget
                  </Typography>
                  <Typography variant="body2" fontWeight="medium">
                    {budgetInfo.usedHours.toFixed(1)} Std. / {budgetInfo.totalBudgetHours.toFixed(1)} Std.
                  </Typography>
                </Box>
                <LinearProgress 
                  variant="determinate" 
                  value={Math.min(budgetInfo.percentageUsed, 100)}
                  color={
                    budgetInfo.percentageUsed >= 90 ? 'error' : 
                    budgetInfo.percentageUsed >= 75 ? 'warning' : 
                    'primary'
                  }
                  sx={{ height: 8, borderRadius: 1 }}
                />
                <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
                  Verbleibend: {budgetInfo.remainingHours.toFixed(1)} Std. ({(100 - budgetInfo.percentageUsed).toFixed(1)}%)
                </Typography>
              </Box>
            )}

            {/* Budget Warning Alert */}
            {warningLevel && (
              <Alert 
                severity={warningLevel.severity} 
                icon={warningLevel.severity === 'error' ? <WarningIcon /> : undefined}
              >
                {warningLevel.message}
              </Alert>
            )}

            {/* Project/Assignment selection */}
            {availableAssignments.length === 0 ? (
              <Alert severity="info">
                Derzeit haben Sie keine Verträge, für die Sie Zeiten erfassen können.
              </Alert>
            ) : (
              <TextField
                select
                label="Projekt auswählen"
                value={selectedAssignment}
                onChange={(e) => setSelectedAssignment(e.target.value)}
                fullWidth
                helperText={`${availableAssignments.length} Projekt(e) verfügbar`}
              >
                {Object.entries(groupedAssignments).map(([projectId, group]) => [
                  <MenuItem key={`header-${projectId}`} disabled>
                    <Typography variant="subtitle2" color="primary" fontWeight="bold">
                      {group.project_name} ({group.account_name})
                    </Typography>
                  </MenuItem>,
                  ...group.assignments.map((assignment) => (
                    <MenuItem 
                      key={assignment.id} 
                      value={assignment.id}
                      disabled={assignment.contract_status !== 'Active' && assignment.contract_status !== 'Draft'}
                    >
                      <Box sx={{ pl: 2 }}>
                        <Typography variant="body2">
                          {assignment.project_name} ({assignment.account_name}) - {assignment.remaining_hours.toFixed(2)}h / {assignment.hours}h @ {assignment.rate}€ remaining
                        </Typography>

                        <Typography variant="caption" color="text.secondary">
                          Status: {assignment.contract_status} {assignment.contract_title && `Vertrag: ${assignment.contract_title}`} - Vertragslaufzeit: {assignment.contract_start} - {assignment.contract_end}
                        </Typography>
                                                
                      </Box>
                    </MenuItem>
                  ))
                ])}
              </TextField>
            )}

            {/* Hours field */}
            <TimeField
              label="Stunden"
              value={hours}
              onChange={(newValue) => setHours(newValue)}
              format="HH:mm"
              ampm={false}
              fullWidth
            />

            {/* Description field */}
            <TextField
              label="Beschreibung"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              multiline
              rows={6}
              fullWidth
            />

            {/* Save button */}
            <Button
              variant="contained"
              color="primary"
              onClick={handleSubmit}
              disabled={!selectedAssignment || !hours || selectedDates.length === 0}
            >
              Zeiteinträge speichern
            </Button>
          </Box>
         </Paper>


        </Box>

    </LocalizationProvider>
  );
};

export default TimeEntryForm;